import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="documents--multi-proforma-picker"
//
// Manages the list of source proformas backing a settlement invoice. The
// user can add/remove proformas; for each selected proforma we maintain
//   1. a hidden <input name="proforma_ids[]"> inside the form, and
//   2. a visible chip pill in the selectedList target.
//
// After every add/remove we dispatch a "documents:proformasChanged" event
// on `document` so the diff-preview controller can refetch the panel.
//
// Action params (read from data-*-param attributes on the click target):
//   - proforma-id      : DB id of the proforma
//   - proforma-number  : human-readable number, used as chip label
export default class extends Controller {
  static targets = ["selectedList", "picker"]
  static values = { diffPreviewId: String }

  add(event) {
    const proformaId = event.params?.proformaId
    const proformaNumber = event.params?.proformaNumber

    if (!proformaId) return
    if (this.#findHiddenInput(proformaId)) return // already selected

    this.#appendHiddenInput(proformaId)
    this.#appendChip(proformaId, proformaNumber)
    this.#notifyChanged()
  }

  remove(event) {
    const proformaId = event.params?.proformaId
    if (!proformaId) return

    this.#removeHiddenInput(proformaId)
    this.#removeChip(proformaId)
    this.#notifyChanged()
  }

  #findHiddenInput(proformaId) {
    const form = this.#findForm()
    if (!form) return null
    return form.querySelector(`input[name="proforma_ids[]"][value="${CSS.escape(String(proformaId))}"]`)
  }

  #appendHiddenInput(proformaId) {
    const form = this.#findForm()
    if (!form) return

    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "proforma_ids[]"
    input.value = String(proformaId)
    input.dataset.proformaIdMarker = String(proformaId)
    form.appendChild(input)
  }

  #removeHiddenInput(proformaId) {
    const input = this.#findHiddenInput(proformaId)
    if (input) input.remove()
  }

  #appendChip(proformaId, proformaNumber) {
    if (!this.hasSelectedListTarget) return
    if (this.selectedListTarget.querySelector(`[data-proforma-id="${CSS.escape(String(proformaId))}"]`)) return

    const chip = document.createElement("span")
    chip.className = "badge text-bg-secondary d-inline-flex align-items-center gap-1 me-1 mb-1"
    chip.dataset.proformaId = String(proformaId)
    chip.textContent = proformaNumber || `#${proformaId}`

    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "btn-close btn-close-white ms-1"
    removeBtn.setAttribute("aria-label", "remove")
    removeBtn.dataset.action = "click->documents--multi-proforma-picker#remove"
    // Namespaced identifiers ("documents--multi-proforma-picker") need the
    // "--" preserved literally — the dataset camelCase setter would collapse
    // it to a single dash, so Stimulus's event.params regex would never match.
    removeBtn.setAttribute("data-documents--multi-proforma-picker-proforma-id-param", String(proformaId))

    chip.appendChild(removeBtn)
    this.selectedListTarget.appendChild(chip)
  }

  #removeChip(proformaId) {
    if (!this.hasSelectedListTarget) return
    const chip = this.selectedListTarget.querySelector(`[data-proforma-id="${CSS.escape(String(proformaId))}"]`)
    if (chip) chip.remove()
  }

  #findForm() {
    // Prefer a form within the same document as the controller; fall back
    // to the first <form> on the page so the picker keeps working when
    // the form lives outside the picker's subtree (common layout).
    return this.element.closest("form") || document.querySelector("form")
  }

  #notifyChanged() {
    const detail = { diffPreviewId: this.diffPreviewIdValue || null }
    document.dispatchEvent(new CustomEvent("documents:proformasChanged", { detail }))
  }
}
